import { writable, type Readable } from "svelte/store";
import { AvailabilityStatus } from "@workadventure/messages";
import type { Subscription, Observable } from "rxjs";
import { gameManager } from "../../Phaser/Game/GameManager";
import { conversationBubblesStore, type ConversationBubbleData } from "./ConversationBubblesStore";

export type ActiveSpaceSource = "bubble" | "map-area";

export type ActiveSpaceParticipant = {
    name: string;
    uuid: string;
    userUuid: string;
    pictureStore: Readable<string | undefined>;
    availabilityStatus: AvailabilityStatus;
    userId?: number;
    spaceUserId?: string;
};

export type ActiveSpaceEntry = {
    id: string;
    source: ActiveSpaceSource;
    zoneName: string | undefined;
    participants: ActiveSpaceParticipant[];
    groupId?: number;
    x?: number;
    y?: number;
    spaceName?: string;
};

function createActiveSpacesStore() {
    const { subscribe, update, set } = writable<Map<string, ActiveSpaceEntry>>(new Map());

    return {
        subscribe,
        set,
        update,
    };
}

export const activeSpacesStore = createActiveSpacesStore();

let bubbleSubscription: (() => void) | undefined;

export function initBubbleTracking(): () => void {
    if (bubbleSubscription) return bubbleSubscription;

    bubbleSubscription = conversationBubblesStore.subscribe(($bubbles) => {
        const bubbleEntries: [string, ActiveSpaceEntry][] = [];
        for (const [groupId, bubble] of $bubbles) {
            if (bubble.userIds.length < 2) continue;
            bubbleEntries.push([`bubble:${groupId}`, convertBubbleToEntry(bubble)]);
        }

        activeSpacesStore.update(($active) => {
            const next = new Map($active);
            for (const key of next.keys()) {
                if (key.startsWith("bubble:")) next.delete(key);
            }
            for (const [key, entry] of bubbleEntries) {
                next.set(key, entry);
            }
            return next;
        });
    });

    return bubbleSubscription;
}

function convertBubbleToEntry(bubble: ConversationBubbleData): ActiveSpaceEntry {
    const participants: ActiveSpaceParticipant[] = bubble.userIds.map((userId) => ({
        userId,
        name: "",
        uuid: "",
        userUuid: "",
        pictureStore: writable(undefined),
        availabilityStatus: AvailabilityStatus.ONLINE,
    }));

    return {
        id: `bubble:${bubble.groupId}`,
        source: "bubble",
        zoneName: undefined,
        participants,
        groupId: bubble.groupId,
        x: bubble.x,
        y: bubble.y,
    };
}

// ---------------------------------------------------------------------------
// Meeting Room Tracking (player position + map areas)
// ---------------------------------------------------------------------------

type MeetingRoomAreaInfo = {
    roomName: string;
    x: number;
    y: number;
    width: number;
    height: number;
};

let cachedMeetingRoomAreas: MeetingRoomAreaInfo[] = [];

export function cacheMeetingRoomAreas(areas: MeetingRoomAreaInfo[]): void {
    cachedMeetingRoomAreas = areas;
}

function getMeetingRoomAtPosition(x: number, y: number): string | null {
    for (const area of cachedMeetingRoomAreas) {
        if (x >= area.x && x <= area.x + area.width && y >= area.y && y <= area.y + area.height) {
            return area.roomName;
        }
    }
    return null;
}

const playerMeetingRoomCache = new Map<number, string | null>();

function getPlayerData(userId: number): { name: string; userUuid: string } | undefined {
    try {
        const scene = gameManager.getCurrentGameScene();
        const player = scene.getRemotePlayersRepository().getPlayers().get(userId);
        if (player) return { name: player.name, userUuid: player.userUuid };
    } catch {
        return undefined;
    }
    return undefined;
}

function getPlayerPictureStore(userId: number): Readable<string | undefined> {
    try {
        const scene = gameManager.getCurrentGameScene();
        return scene.MapPlayersByKey.getNestedStore(userId, (item) => item.pictureStore);
    } catch {
        return writable(undefined);
    }
}

function updateMeetingRoomEntries(): void {
    const roomToPlayers = new Map<string, ActiveSpaceParticipant[]>();

    for (const [userId, roomName] of playerMeetingRoomCache) {
        if (roomName === null) continue;

        const playerData = getPlayerData(userId);
        if (!playerData) continue;

        let list = roomToPlayers.get(roomName);
        if (!list) {
            list = [];
            roomToPlayers.set(roomName, list);
        }
        list.push({
            userId,
            name: playerData.name,
            uuid: playerData.userUuid,
            userUuid: playerData.userUuid,
            pictureStore: getPlayerPictureStore(userId),
            availabilityStatus: AvailabilityStatus.ONLINE,
        });
    }

    // Add local player if in a room
    try {
        const scene = gameManager.getCurrentGameScene();
        const localId = scene.connection?.getUserId();
        if (localId !== undefined) {
            const localRoom = playerMeetingRoomCache.get(localId);
            if (localRoom !== undefined && localRoom !== null) {
                const localUser = scene.CurrentPlayer;
                if (localUser) {
                    let list = roomToPlayers.get(localRoom);
                    if (!list) {
                        list = [];
                        roomToPlayers.set(localRoom, list);
                    }
                    const alreadyInList = list.some((p) => p.userId === localId);
                    if (!alreadyInList) {
                        list.push({
                            userId: localId,
                            name: "",
                            uuid: "",
                            userUuid: "",
                            pictureStore: localUser.pictureStore,
                            availabilityStatus: AvailabilityStatus.ONLINE,
                        });
                    }
                }
            }
        }
    } catch {
        return;
    }

    activeSpacesStore.update(($active) => {
        const next = new Map($active);
        for (const key of next.keys()) {
            if (key.startsWith("map-area:")) next.delete(key);
        }
        for (const [roomName, participants] of roomToPlayers) {
            if (participants.length < 2) continue;
            next.set(`map-area:${roomName}`, {
                id: `map-area:${roomName}`,
                source: "map-area",
                zoneName: roomName,
                participants,
                spaceName: roomName,
            });
        }
        return next;
    });
}

let meetingRoomTrackingUnsubscriber: (() => void) | undefined;

export function initMeetingRoomTracking(
    userJoinedStream: Observable<{ userId: number; position: { x: number; y: number } }>,
    userMovedStream: Observable<{ userId: number; position?: { x: number; y: number } }>,
    userLeftStream: Observable<{ userId: number }>,
): () => void {
    if (meetingRoomTrackingUnsubscriber) return meetingRoomTrackingUnsubscriber;

    const subs: Subscription[] = [];

    subs.push(
        userJoinedStream.subscribe((message) => {
            const room = getMeetingRoomAtPosition(message.position.x, message.position.y);
            playerMeetingRoomCache.set(message.userId, room);
            updateMeetingRoomEntries();
        }),
    );

    subs.push(
        userMovedStream.subscribe((message) => {
            if (!message.position) return;
            const prevRoom = playerMeetingRoomCache.get(message.userId);
            const newRoom = getMeetingRoomAtPosition(message.position.x, message.position.y);
            if (prevRoom !== newRoom) {
                playerMeetingRoomCache.set(message.userId, newRoom);
                updateMeetingRoomEntries();
            }
        }),
    );

    subs.push(
        userLeftStream.subscribe((message) => {
            playerMeetingRoomCache.delete(message.userId);
            updateMeetingRoomEntries();
        }),
    );

    // Initial scan of current remote players
    try {
        const scene = gameManager.getCurrentGameScene();
        const remotePlayers = scene.getRemotePlayersRepository().getPlayers();
        for (const [userId, player] of remotePlayers) {
            const room = getMeetingRoomAtPosition(player.position.x, player.position.y);
            playerMeetingRoomCache.set(userId, room);
        }
    } catch {
        // scene not ready
    }

    updateMeetingRoomEntries();

    meetingRoomTrackingUnsubscriber = () => {
        for (const sub of subs) sub.unsubscribe();
        playerMeetingRoomCache.clear();
        meetingRoomTrackingUnsubscriber = undefined;
    };

    return meetingRoomTrackingUnsubscriber;
}

export function updateLocalPlayerPosition(userId: number, x: number, y: number): void {
    const room = getMeetingRoomAtPosition(x, y);
    playerMeetingRoomCache.set(userId, room);
    updateMeetingRoomEntries();
}

export function cleanupMeetingRoomTracking(): void {
    if (meetingRoomTrackingUnsubscriber) meetingRoomTrackingUnsubscriber();
    cachedMeetingRoomAreas = [];
}
